"use client";

import FuseLoading from "@fuse/core/FuseLoading";
import FusePageCarded from "@fuse/core/FusePageCarded";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { motion } from "motion/react";
import { useEffect } from "react";
import { useParams } from "react-router-dom";
import Link from "@fuse/core/Link";
import _ from "lodash";
import { FormProvider, useForm } from "react-hook-form";
import useThemeMediaQuery from "@fuse/hooks/useThemeMediaQuery";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import  AreaHeader from "./AreaHeader";
import AreaInfo from "./AreaInfo";
import { useGetAreaQuery } from "@/store/services/areas";

/**
 * Form Validation Schema
 */
const schema = z.object({
  name: z.string().nonempty("You must enter a area name"),
  lineItems: z
    .array(z.string().min(1, "Line item cannot be empty"))
    .min(1, "At least one line item is required."),
});


/**
 * The rea page.
 */
function Area() {
  const isMobile = useThemeMediaQuery((theme) => theme.breakpoints.down("lg"));

  const routeParams = useParams<{ areaId: string }>();

  const { areaId } = routeParams;

  const methods = useForm({
    mode: "onChange",
    defaultValues: {},
    resolver: zodResolver(schema),
  });

  const { reset } = methods; 

  const {
    data: area,
    isLoading,
    isError,
  } = useGetAreaQuery(areaId, {
    skip: !areaId || areaId === "new",
    refetchOnMountOrArgChange: true
  },);

  useEffect(() => {
    if (areaId === "new") {
      reset({ name: '', lineItems: [], is_used: false, prevlineItems: [] });
    }
  }, [areaId, reset]);

  useEffect(() => {
    if (area) {
      reset({ 
        name: area?.data?.name, 
        lineItems: area?.data?.line_items?.map((item: any) =>
          typeof item === 'string' ? item : item?.name
        )?.filter((x): x is string => typeof x === 'string') ?? [],
        prevlineItems: area?.data?.line_items,
        is_used: area?.data?.is_used ?? false,
      })
    }
  }, [area, reset]);

  if (isLoading) {
    return <FuseLoading />;
  }

  /**
   * Show Message if the requested rea is not exists
   */

  if ((!area?.data?.id && areaId !== "new") || isError) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1, transition: { delay: 0.1 } }}
        className="flex flex-col flex-1 items-center justify-center h-full"
      >
        <Typography color="text.secondary" variant="h5">
          There is no such area!
        </Typography>
        <Button
          className="mt-6"
          component={Link}
          variant="outlined"
          to="/apps/areas"
          color="inherit"
        >
          Go to Areas Page
        </Button>
      </motion.div>
    );
  }


  return (
    <FormProvider {...methods}>
      <FusePageCarded
        header={<AreaHeader />}
        content={
          <div className="p-4 sm:p-6 max-w-5xl space-y-6">
              <AreaInfo />
          </div>
        }
        scroll={isMobile ? "normal" : "content"}
      />
    </FormProvider>
  );
}

export default Area;
